import { makeAutoObservable, runInAction } from 'mobx'
import { uniqBy } from 'lodash'
import { type ActionItem, Avatar, Icon, showToast, Typography } from 'shared/ui'
import { type IColumn, type IRowDefault } from 'shared/ui/TableNew/store/types'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { LINK_MAKE_INBOX_OWNER } from 'shared/constants/links'
import { logger } from 'shared/lib'
import { TableStore } from 'shared/ui/TableNew'
import { uiStore } from 'shared/store/uiStore'
import { InboxesApi } from 'entities/Inbox'
import {
  type IResponseInboxBulkUserAttached,
  type IResponseInboxBulkUserRemoved,
} from 'entities/Inbox/api/types'
import { type IResponseUser } from 'entities/Users/api/types'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { WebsocketEvent } from 'entities/WebSocket'
import { checkAccessPage, EnumAccessPageKeys } from 'entities/ExportRouters/lib/checkAccessPage'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { UsersApi } from 'entities/Users'
import { AddMemberStore, type IAddMemberStoreConfig } from 'widgets/AddMembers'
import { PermissionChip } from 'pages/settings/pages/inboxes/pages/id/pages/members/ui/PermissionChip/PermissionChip'
import { LearnMoreLink } from 'pages/settings/ui/LearnMoreLink/LearnMoreLink'
import { ChangeOwnerModalContent } from 'pages/settings/pages/inboxes/pages/id/pages/members/ui/ChangeOwnerModalContent/ChangeOwnerModalContent'
import { InboxesPath } from 'pages/settings/pages/inboxes/route/inboxesPath'
import { runBeforeAddMember } from 'pages/settings/lib/runBeforeAddMember'
import styles from './styles.module.scss'
import { type InboxLayerStore } from './inboxLayerStore'

export type IMembersRow = IRowDefault & {
  fields: IResponseUser
}

export class MembersStore {
  private _addMemberStore: AddMemberStore<IResponseUser>
  private _tableStore: TableStore<IMembersRow>
  private _members: IResponseUser[] = []
  private _loading = false
  private _organizationMembersLoading = false
  private _search = ''
  private _currentInboxId: number | null = null
  private _organizationMembers: IResponseUser[] = []
  private _newOwner: IResponseUser | null = null
  private _modalId: string | null = null
  private _page = 1
  private _perPage = 20
  private _loadingMore = false
  private _hasMore = false
  private _waitingForMemberRemovedUnsubscribe: (() => void) | null = null
  private _waitingForMemberAddedUnsubscribe: (() => void) | null = null

  constructor(readonly inboxLayerStore: InboxLayerStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this.loadOrganizationMembers()
    this._tableStore = new TableStore({
      key: 'inbox-members-table',
    })
    this._addMemberStore = new AddMemberStore<IResponseUser>(this._getAddMemberConfig())
  }

  private _getAddMemberConfig = (): IAddMemberStoreConfig<IResponseUser> => ({
    getExistingMemberIds: () => this.fullInboxMembersIdsList,
    getOrganizationMembers: () => this._organizationMembers,
    getOrganizationMembersLoading: () => this._organizationMembersLoading,
    submitAddMembers: async (selected) => {
      const inboxId = this.inboxId
      if (inboxId === null || inboxId === undefined) return
      if (selected.length > 20) {
        const { data } = await InboxesApi.updateTeamsByIdMembers(inboxId, {
          members: selected.map((m) => ({ id: m.id })),
        })
        if (
          !Array.isArray(data) &&
          data?.data?.chunks_count !== null &&
          data?.data?.chunks_count !== undefined
        ) {
          return { chunksCount: data.data.chunks_count }
        }
      } else {
        await InboxesApi.updateTeamsByIdMembers(inboxId, {
          members: selected.map((m) => ({ id: m.id })),
        })
      }
    },
    onSuccess: async () => {
      const inboxId = this.inboxId
      if (inboxId !== null && inboxId !== undefined) await this.inboxLayerStore.init(inboxId)
    },
    waitForBulk: (chunkCount) => this._waitForInboxBulkAttached(chunkCount),
    onClear: () => {
      this._waitingForMemberAddedUnsubscribe?.()
      this._waitingForMemberAddedUnsubscribe = null
    },
    successMessage: 'Members added to inbox',
  })

  private _waitForInboxBulkAttached = (chunkCount: number): Promise<void> => {
    return new Promise((resolve) => {
      const inboxId = this.inboxId
      if (inboxId === null || inboxId === undefined) {
        resolve()
        return
      }
      this._waitingForMemberAddedUnsubscribe?.()
      this._waitingForMemberAddedUnsubscribe = null
      const timeout = setTimeout(() => {
        this._waitingForMemberAddedUnsubscribe?.()
        this._waitingForMemberAddedUnsubscribe = null
        resolve()
      }, 10000)
      this._waitingForMemberAddedUnsubscribe = this._entities.websocket.on(
        WebsocketEvent.InboxBulkUserAttached,
        (data: IResponseInboxBulkUserAttached) => {
          if (data.team.id === inboxId && data.index === chunkCount) {
            clearTimeout(timeout)
            this._waitingForMemberAddedUnsubscribe?.()
            this._waitingForMemberAddedUnsubscribe = null
            resolve()
          }
        }
      )
    })
  }

  addNewMembersClick = () => {
    runBeforeAddMember(this._entities, () => {
      this._addMemberStore.openAddMembersModal()
    })
  }

  get entities() {
    return this._entities
  }

  get tableStore() {
    return this._tableStore
  }

  get addMemberStore() {
    return this._addMemberStore
  }

  get loading() {
    return this._loading
  }

  get loadingMore() {
    return this._loadingMore
  }

  get modalId() {
    return this._modalId
  }

  get organizationMembersLoading() {
    return this._organizationMembersLoading
  }

  get search() {
    return this._search
  }

  get newOwner() {
    return this._newOwner
  }

  get members() {
    return this._members
  }

  get fullInboxMembersIdsList() {
    return this.inboxLayerStore.currentTeam?.member_ids || []
  }

  get isHasMore() {
    return this._hasMore
  }

  get page() {
    return this._page
  }

  get noResultsFound() {
    return (
      !this.membersTableRows.length &&
      !this.loading &&
      !this.loadingMore &&
      this.search.trim() !== ''
    )
  }

  get filteredMembers() {
    if (!this._search.trim()) {
      return this._members
    }

    const searchTerm = this._search.toLowerCase()
    return this._members.filter((member) => {
      const fullName = member.full_name?.toLowerCase()
      const email = member.email?.toLowerCase()

      return fullName?.includes(searchTerm) || email?.includes(searchTerm)
    })
  }

  get organizationMembers() {
    return this._organizationMembers
  }

  get ownerMember() {
    return this._members.find((member) => member.team_role === 'owner')
  }

  get nonOwnerAndViewOnlyMembers() {
    const owner = this.ownerMember

    return this._organizationMembers.filter(
      (member) => member.id !== owner?.id && member.role !== 'view_only'
    )
  }

  get inboxId() {
    return this.currentInbox?.id
  }

  get currentInbox() {
    const inbox = this._entities.inboxesStore.getItem(this._currentInboxId)

    if (inbox instanceof Inbox) {
      return inbox
    }
    return null
  }
  getRowActions = (member: IResponseUser): ActionItem[] => {
    const isOwner = member.team_role === 'owner'
    const isViewOnly = member.user_role === 'view_only'

    if (isOwner) {
      return [
        {
          icon: 'inboxOpen',
          text: 'Change inbox owner',
          onAction: () => this.openChangeInboxOwnerModal(member),
        },
      ]
    }

    return [
      {
        icon: 'inboxOpen',
        text: 'Make inbox owner',
        disabled: isViewOnly,
        buttonProps: {
          disabled: isViewOnly,
          tooltipProps: {
            label: "Members with the Observer role can't be inbox owners.",
            placement: 'top',
            width: '220px',
          },
        },
        onAction: () => this.openMakeInboxOwnerModal(member),
      },
      {
        icon: 'delete',
        text: 'Remove member',
        onAction: () => this.openDeleteMemberModal([member.id]),
      },
    ]
  }

  get membersTableRows(): IMembersRow[] {
    return this.filteredMembers.map((member) => {
      const actions = this.getRowActions(member)

      return {
        id: member.id,
        expanded: false,
        is_can_expanded: false,
        isMoreExpand: false,
        loadingMore: false,
        loading: false,
        actionsProps: null,
        actions,
        isDisabled: false,
        isCheckboxDisabled: false,
        ariaLabel: `Member ${member.full_name || member.first_name}`,
        columnsKeys: ['name', 'permission'],
        setColumnsKeys: () => {},
        fields: member,
        withActions: true,
        origin: member,
      }
    })
  }

  get getMembersTableColumns(): IColumn<IMembersRow>[] {
    return [
      {
        field: 'name',
        name: 'Name',
        width: '300px',
        maxWidth: '300px',
        renderRowCell: (row) => {
          const user = row.fields
          return (
            <div className={styles.nameInfoContainer}>
              <Avatar
                online={!user.is_away}
                info={{
                  image: user.photo_url,
                  firstName: user.first_name,
                  lastName: user.last_name,
                }}
                iconProps={{
                  fontSize: 32,
                }}
                size={32}
              />
              <div className={styles.nameContainer}>
                <Typography
                  variant='body-md-regular'
                  ariaLabel='member-name'
                  width={'210px'}
                  color='var(--content-primary-primary)'
                  ellipsis
                >
                  {user.full_name}
                </Typography>
                <Typography
                  variant='body-sm-regular'
                  color='var(--content-primary-tertiary)'
                  ariaLabel='member-email'
                  width={'210px'}
                  ellipsis
                >
                  {user.email}
                </Typography>
              </div>
              {user.team_role === 'owner' && (
                <Icon
                  icon='flagFilled'
                  color='var(--content-attention-primary)'
                  className={styles.ownerIcon}
                />
              )}
            </div>
          )
        },
      },
      {
        field: 'permission',
        name: 'Permission',
        width: '370px',
        renderRowCell: (row) => {
          const user = row.fields
          return <PermissionChip permission={user.team_role} />
        },
      },
    ]
  }

  init = async (inboxId: number) => {
    this._currentInboxId = inboxId
    await this.loadMembers(inboxId, true)
  }

  setModalId = (modalId: string | null) => {
    this._modalId = modalId
  }

  removeModal = () => {
    if (this._modalId) {
      modalStore.removeModal(this._modalId)
      this.setModalId(null)
    }
  }

  setSearch = (search: string) => {
    this._search = search
  }

  setNewOwner = (ownerId: string | null) => {
    if (!ownerId) {
      this._newOwner = null
      return
    }

    this._newOwner =
      this.organizationMembers.find((member) => member.id === Number(ownerId)) || null
  }

  deleteMembersBulk = async (membersIds: number[]) => {
    if (!this.inboxId) return

    try {
      const deletableMemberIds: number[] = []
      const nonDeletableMembers: IResponseUser[] = []

      for (const memberId of membersIds) {
        const member = this._members.find((m) => m.id === memberId)

        if (!member) {
          continue
        }

        const cannotRemove = member.cannot_remove || []
        if (cannotRemove.includes(this.inboxId)) {
          nonDeletableMembers.push(member)
        } else {
          deletableMemberIds.push(memberId)
        }
      }

      if (nonDeletableMembers.length > 0) {
        const memberNames = nonDeletableMembers
          .map((m) => m.full_name || m.first_name || m.email)
          .join(', ')

        const errorMessage =
          nonDeletableMembers.length === 1
            ? `Can’t remove ${memberNames}`
            : `Can’t remove ${nonDeletableMembers.length} members from this inbox: ${memberNames}`
        const desc =
          nonDeletableMembers.length === 1
            ? 'This member is part of any IVR, Call Distribution, or Conversation Assignment settings. Remove them from those first.'
            : 'These members are part of any IVR, Call Distribution, or Conversation Assignment settings. Remove them from those first.'

        showToast({
          title: errorMessage,
          desc,
          type: 'error',
        })
      }

      if (deletableMemberIds.length === 0) {
        this.removeModal()

        return
      }

      const { data } = await InboxesApi.deleteMembersBulk(this.inboxId, deletableMemberIds)

      if (!Array.isArray(data)) {
        await this.waitForMemberRemoved(data.data.chunks_count)
      }

      this.removeModal()

      const successMessage = `${
        deletableMemberIds.length > 1 ? `${deletableMemberIds.length} members` : 'Member'
      } removed`
      showToast({
        title: successMessage,
        type: 'success',
      })
      this._tableStore.resetSelected()
      await this.inboxLayerStore.init(this.inboxId)
    } catch (error) {
      logger.error(error)
      this._waitingForMemberRemovedUnsubscribe?.()
      this._waitingForMemberRemovedUnsubscribe = null
    }
  }

  private waitForMemberRemoved = (chunkCount: number): Promise<void> => {
    return new Promise((resolve) => {
      const inboxId = this.inboxId
      if (!inboxId) {
        resolve()
        return
      }

      this._waitingForMemberRemovedUnsubscribe?.()
      this._waitingForMemberRemovedUnsubscribe = null

      const timeout = setTimeout(() => {
        this._waitingForMemberRemovedUnsubscribe?.()
        this._waitingForMemberRemovedUnsubscribe = null
        resolve()
      }, 10000)

      this._waitingForMemberRemovedUnsubscribe = this._entities.websocket.on(
        WebsocketEvent.InboxBulkUserRemoved,
        (data: IResponseInboxBulkUserRemoved) => {
          if (data.team.id === inboxId && data.index === chunkCount) {
            clearTimeout(timeout)
            this._waitingForMemberRemovedUnsubscribe?.()
            this._waitingForMemberRemovedUnsubscribe = null
            resolve()
          }
        }
      )
    })
  }

  openDeleteMemberModal = (memberIds: number[]) => {
    this.setModalId('delete-member-modal')

    const title =
      memberIds.length > 1
        ? `Remove ${memberIds.length} members from this inbox?`
        : 'Remove member from this inbox?'

    modalStore.addModal({
      id: this.modalId || '',
      type: ModalTypeList.ALERT,
      title: (
        <Typography variant={'heading-md'} ariaLabel={'title'} className={styles.modalTitle}>
          {title}
        </Typography>
      ),
      width: 280,
      primaryAction: {
        text: 'Remove',
        onAction: () => this.deleteMembersBulk(memberIds),
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => this.removeModal(),
      },
    })
  }

  changeInboxOwner = async (member: IResponseUser) => {
    if (!this.inboxId) return

    try {
      await InboxesApi.changeInboxOwner(this.inboxId, {
        owner_id: member.id,
      })

      this.removeModal()

      showToast({
        title: 'Inbox owner changed',
        type: 'success',
      })

      this.currentInbox?.changeOwnerId(member.id)
      this.checkUserAccess()
      this.loadMembers(this.inboxId, true)
    } catch (error) {
      logger.error(error)
    }
  }

  checkUserAccess = () => {
    const isInboxOwner = this.currentInbox?.ownerId === this._entities.usersStore.user?.id
    const isManageOrganizationInboxes = checkAccessPage(
      EnumAccessPageKeys.inbox_page,
      this._entities
    )

    if (!isInboxOwner && !isManageOrganizationInboxes) {
      uiStore.changeRouteViaVue({
        path: `/${InboxesPath.root}`,
      })
    }
  }

  openMakeInboxOwnerModal = (member: IResponseUser) => {
    this.setModalId('make-inbox-owner-modal')

    modalStore.addModal({
      id: this.modalId || '',
      type: ModalTypeList.INFO,
      title: 'Make inbox owner?',
      width: 280,
      desc: (
        <div>
          <Typography
            ariaLabel='make-inbox-owner-modal-desc'
            variant='body-rg-regular'
            color='var(--content-primary-tertiary)'
          >
            Are you sure you want to make {member.full_name} the inbox owner of &nbsp;
            {this.currentInbox?.name || ''}?
            <br />
            <LearnMoreLink href={LINK_MAKE_INBOX_OWNER}>Learn more</LearnMoreLink>
          </Typography>
        </div>
      ),
      primaryAction: {
        text: 'Yes, change owner',
        onAction: () => this.changeInboxOwner(member),
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => this.removeModal(),
      },
    })
  }

  handleMakeOwner = async () => {
    if (this._newOwner) {
      await this.changeInboxOwner(this._newOwner)

      this.removeModal()
    }
  }

  handleCancel = () => {
    this.setNewOwner(null)
    this.removeModal()
  }

  openChangeInboxOwnerModal = (member: IResponseUser) => {
    this.setModalId('change-inbox-owner-modal')

    modalStore.addModal({
      id: this.modalId || '',
      title: 'Change inbox owner',
      width: 480,
      showCloseButton: false,
      paddingContent: '0px 24px 24px 24px',
      ModalContentProps: {
        store: this,
        member,
      },
      ModalContent: ChangeOwnerModalContent,
    })
  }

  loadMembers = async (inboxId: number, reset = false) => {
    if (reset) {
      runInAction(() => {
        this._page = 1
        this._members = []
        this._loading = true
        this._hasMore = false
      })
    } else {
      runInAction(() => {
        this._loadingMore = true
      })
    }

    try {
      const { data } = await InboxesApi.getTeamByIdMembers2_0(inboxId, {
        per_page: this._perPage,
        page: this._page,
      })

      runInAction(() => {
        if (reset) {
          this._members = data.data
        } else {
          this._members = uniqBy([...this._members, ...data.data], 'id')
        }

        this._hasMore = data.meta.total > this._members.length
      })
    } catch (error) {
      console.error(error)

      runInAction(() => {
        if (reset) {
          this._loading = false
        } else {
          this._loadingMore = false
        }
        this._hasMore = false
      })
      throw error
    } finally {
      runInAction(() => {
        if (reset) {
          this._loading = false
        } else {
          this._loadingMore = false
        }
      })
    }
  }

  loadMore = async () => {
    if (!this.inboxId || this._loadingMore || !this._hasMore) {
      return
    }

    runInAction(() => {
      this._page += 1
    })

    await this.loadMembers(this.inboxId, false)
  }

  loadOrganizationMembers = async () => {
    runInAction(() => {
      this._organizationMembersLoading = true
    })

    try {
      const users = await UsersApi.getOrganizationMembers()

      runInAction(() => {
        this._organizationMembers = users.data
        this._organizationMembersLoading = false
      })
    } catch (error) {
      logger.error(error)
      runInAction(() => {
        this._organizationMembersLoading = false
      })
    }
  }

  loadCurrentTeam = async (inboxId: number) => {
    await this.inboxLayerStore.loadTeam(inboxId)
  }

  dispose = () => {
    runInAction(() => {
      this._members = []
      this._page = 1
      this._hasMore = false
      this._waitingForMemberRemovedUnsubscribe?.()
      this._waitingForMemberRemovedUnsubscribe = null
    })
  }
}
